import React from "react";
import { connect } from "react-redux";
import api from "../network/api";
import "./ChatWidget.css";

const SESSION_ID = "web";

class ChatWidget extends React.Component {
  state = {
    open: false,
    input: "",
    sending: false,
    messages: [
      { role: "assistant", text: "Hi! I'm your MoneyApp assistant. Ask me about appointments, invoices, clients or setting up your workflows." },
    ],
  };

  endRef = React.createRef();

  // api methods expect a getState-style fn; provide auth.user + the selected
  // company so the chat backend can scope its tools to the current company.
  getState = () => ({
    auth: { user: this.props.user },
    companies: { selectedCompanyId: this.props.companyId, company: this.props.company },
  });

  scrollDown = () => {
    if (this.endRef.current) this.endRef.current.scrollIntoView({ behavior: "smooth" });
  };

  componentDidUpdate(_, prev) {
    if (prev.messages !== this.state.messages || prev.open !== this.state.open) this.scrollDown();
  }

  toggle = () => this.setState((s) => ({ open: !s.open }));

  send = async () => {
    const text = this.state.input.trim();
    if (!text || this.state.sending) return;
    this.setState((s) => ({
      messages: [...s.messages, { role: "user", text }],
      input: "",
      sending: true,
    }));
    try {
      const res = await api.aiChat(this.getState, text, SESSION_ID);
      const reply =
        res && res.response
          ? res.response
          : res && res.error
          ? res.error
          : "The assistant isn't available right now.";
      const attachments = res && Array.isArray(res.attachments) ? res.attachments : [];
      this.setState((s) => ({ messages: [...s.messages, { role: "assistant", text: reply, attachments }], sending: false }));
    } catch (e) {
      this.setState((s) => ({
        messages: [...s.messages, { role: "assistant", text: "Sorry — I couldn't reach the assistant. Please try again." }],
        sending: false,
      }));
    }
  };

  // Fetch a tool-attached document with the user's auth headers and open it.
  // Plain fetch on purpose: request() appends date-range query params to every
  // url, which the download endpoints don't expect.
  openAttachment = async (att) => {
    const urls = require("../network/urls").default;
    const user = this.props.user || {};
    try {
      const res = await fetch(urls.getBase() + att.url, {
        headers: { username: user.username, token: user.token, AUTHORIZATION: user.token },
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      const win = window.open(objectUrl, "_blank");
      if (!win) {
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = att.name || "document.pdf";
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (e) {
      this.setState((s) => ({
        messages: [...s.messages, { role: "assistant", text: "Sorry — I couldn't fetch that document. Please try again." }],
      }));
    }
  };

  clear = async () => {
    this.setState({ messages: [{ role: "assistant", text: "Cleared. How can I help?" }] });
    try { await api.aiChatClear(this.getState, SESSION_ID); } catch (e) { /* non-fatal */ }
  };

  onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this.send();
    }
  };

  render() {
    const { open, input, sending, messages } = this.state;
    const { company } = this.props;
    const companyLabel = company && company.companyName ? company.companyName : "No company selected";
    return (
      <div className="cw">
        {open && (
          <div className="cw-panel">
            <div className="cw-head">
              <span className="cw-head-title">
                <span><i className="fas fa-robot" /> MoneyApp Assistant</span>
                <span className="cw-head-company">
                  <i className="fas fa-building" style={{ fontSize: 9, opacity: 0.7 }} /> {companyLabel}
                </span>
              </span>
              <span className="cw-head-actions">
                <button type="button" className="cw-icon-btn" title="Clear chat" onClick={this.clear}>
                  <i className="fas fa-trash-alt" />
                </button>
                <button type="button" className="cw-icon-btn" title="Close" onClick={this.toggle}>
                  <i className="fas fa-times" />
                </button>
              </span>
            </div>

            <div className="cw-body">
              {messages.map((m, i) => (
                <div key={i} className={`cw-msg cw-msg-${m.role}`}>
                  {m.text}
                  {m.attachments && m.attachments.length > 0 && (
                    <div className="cw-atts">
                      {m.attachments.map((att, j) => (
                        <button
                          key={j}
                          type="button"
                          className="cw-att"
                          title={`Open ${att.name || "document"}`}
                          onClick={() => this.openAttachment(att)}
                        >
                          <i className="fas fa-file-pdf" />
                          <span className="cw-att-info">
                            <span className="cw-att-title">{att.title || att.name || "Document"}</span>
                            <span className="cw-att-sub">PDF · tap to open</span>
                          </span>
                          <i className="fas fa-download" style={{ opacity: 0.6 }} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {sending && (
                <div className="cw-msg cw-msg-assistant cw-typing">
                  <span /><span /><span />
                </div>
              )}
              <div ref={this.endRef} />
            </div>

            <div className="cw-input">
              <textarea
                rows={1}
                placeholder="Ask anything…"
                value={input}
                onChange={(e) => this.setState({ input: e.target.value })}
                onKeyDown={this.onKeyDown}
              />
              <button type="button" className="cw-send" disabled={!input.trim() || sending} onClick={this.send}>
                <i className="fas fa-paper-plane" />
              </button>
            </div>
          </div>
        )}

        <button type="button" className={`cw-fab ${open ? "open" : ""}`} onClick={this.toggle} aria-label="Open assistant">
          <i className={open ? "fas fa-chevron-down" : "fas fa-comment-dots"} />
        </button>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  user: state.auth.user,
  companyId: state.companies.selectedCompanyId,
  company: state.companies.company,
});
export default connect(mapStateToProps)(ChatWidget);
